import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';


const CarDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { route: routeParam, from, to, vehicle: vehicleName, price, seats, luggage, image, alt } = location?.state || {};
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!from || !to || !vehicleName) {
      navigate('/route-selection');
    }
  }, [from, to, vehicleName, navigate]);

  const carSpecifications = {
    'Maruti Dzire': {
      seatingCapacity: '4+1',
      ac: 'Yes',
      rearAcVents: 'Yes',
      fuelType: 'CNG & Petrol',
      luggageCarrier: 'Yes',
      phoneCharger: 'Yes',
      musicSystem: 'Yes (Bluetooth & Aux)',
      smokingAllowed: 'No',
      petAllowed: 'Yes (INR-300 Extra)',
      airportParking: 'Airport parking fees are not included in the base fare.'
    },
    'Brezza': {
      seatingCapacity: '4+1',
      ac: 'Yes',
      rearAcVents: 'Yes',
      fuelType: 'Petrol',
      luggageCarrier: 'Yes',
      phoneCharger: 'Yes',
      musicSystem: 'Yes (Bluetooth & Aux)',
      smokingAllowed: 'No',
      petAllowed: 'Yes (INR-300 Extra)',
      airportParking: 'Airport parking fees are not included in the base fare.'
    },
    'Maruti Ertiga': {
      seatingCapacity: '6+1',
      ac: 'Yes',
      rearAcVents: 'Yes',
      fuelType: 'CNG & Petrol',
      luggageCarrier: 'Yes',
      phoneCharger: 'Yes',
      musicSystem: 'Yes (Bluetooth & Aux)',
      smokingAllowed: 'No',
      petAllowed: 'Yes (INR-300 Extra)',
      airportParking: 'Airport parking fees are not included in the base fare.'
    },
    'Kia Carens': {
      seatingCapacity: '6+1',
      ac: 'Yes',
      rearAcVents: 'Yes',
      fuelType: 'Petrol & Diesel',
      luggageCarrier: 'Yes',
      phoneCharger: 'Yes',
      musicSystem: 'Yes (Bluetooth & Aux)',
      smokingAllowed: 'No',
      petAllowed: 'Yes (INR-300 Extra)',
      airportParking: 'Airport parking fees are not included in the base fare.'
    },
    'Innova Crysta': {
      seatingCapacity: '6+1',
      ac: 'Yes',
      rearAcVents: 'Yes',
      fuelType: 'Diesel',
      luggageCarrier: 'Yes',
      phoneCharger: 'Yes',
      musicSystem: 'Yes (Bluetooth & Aux)',
      smokingAllowed: 'No',
      petAllowed: 'Yes (INR-300 Extra)',
      airportParking: 'Airport parking fees are not included in the base fare.'
    },
    'Innova Crysta 7+1': {
      seatingCapacity: '7+1',
      ac: 'Yes',
      rearAcVents: 'Yes',
      fuelType: 'Diesel',
      luggageCarrier: 'Yes',
      phoneCharger: 'Yes',
      musicSystem: 'Yes (Bluetooth & Aux)',
      smokingAllowed: 'No',
      petAllowed: 'Yes (INR-300 Extra)',
      airportParking: 'Airport parking fees are not included in the base fare.'
    },
    'Innova Hycross': {
      seatingCapacity: '6+1',
      ac: 'Yes',
      rearAcVents: 'Yes',
      fuelType: 'Hybrid',
      luggageCarrier: 'Yes',
      phoneCharger: 'Yes',
      musicSystem: 'Yes (Bluetooth & Aux)',
      smokingAllowed: 'No',
      petAllowed: 'Yes (INR-300 Extra)',
      airportParking: 'Airport parking fees are not included in the base fare.'
    }
  };

  const specs = carSpecifications?.[vehicleName] || carSpecifications?.['Maruti Dzire'];

  const handleBookNow = () => {
    navigate('/self-drive-enquiry', {
      state: {
        from: from,
        to: to,
        vehicle: {
          name: vehicleName,
          price: price,
          image: image,
          imageAlt: alt
        },
        price: price
      }
    });
  };

  const handleWhatsAppCall = () => {
    const message = `Hi, I'm interested in booking ${vehicleName} from ${from} to ${to} at ₹${price?.toLocaleString('en-IN')}`;
    window.open(`https://wa.me/917998666660?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (!from || !to || !vehicleName) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col">
      <Header />
      <main className="flex-1 pt-20 md:pt-24">
        <div className="bg-gradient-journey text-gray-900 py-4 md:py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold">
              {from} to {to} {vehicleName} Cab Fare ₹{price?.toLocaleString('en-IN')}
            </h1>
          </div>
        </div>

        <section className="py-4 md:py-6 lg:py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              {/* Left Side - Car Image and Specifications */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 md:p-8">
                  {/* Car Image */}
                  <div className="bg-white rounded-lg mb-6 flex items-center justify-center p-4">
                    <Image
                      src={image}
                      alt={alt}
                      className="w-full max-w-2xl h-auto object-contain"
                    />
                  </div>
                  
                  {/* Car Name */}
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-gray-900 mb-6">
                    {vehicleName}
                  </h2>
                  
                  {/* Specifications Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div className="p-3 border border-gray-200 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Seating Capacity :</p>
                      <p className="text-sm font-semibold text-gray-900">{specs?.seatingCapacity}</p>
                    </div>
                    
                    <div className="p-3 border border-gray-200 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">AC :</p>
                      <p className="text-sm font-semibold text-gray-900">{specs?.ac}</p>
                    </div>
                    
                    <div className="p-3 border border-gray-200 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Rear Ac Vents :</p>
                      <p className="text-sm font-semibold text-gray-900">{specs?.rearAcVents}</p>
                    </div>
                    
                    <div className="p-3 border border-gray-200 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Fuel Type :</p>
                      <p className="text-sm font-semibold text-gray-900">{specs?.fuelType}</p>
                    </div>
                    
                    <div className="p-3 border border-gray-200 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Luggage Carrier :</p>
                      <p className="text-sm font-semibold text-gray-900">{specs?.luggageCarrier}</p>
                    </div>
                    
                    <div className="p-3 border border-gray-200 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Phone Charger :</p>
                      <p className="text-sm font-semibold text-gray-900">{specs?.phoneCharger}</p>
                    </div>
                    
                    <div className="p-3 border border-gray-200 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Music System :</p>
                      <p className="text-sm font-semibold text-gray-900">{specs?.musicSystem}</p>
                    </div>
                    
                    <div className="p-3 border border-gray-200 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Smoking Allowed :</p>
                      <p className="text-sm font-semibold text-gray-900">{specs?.smokingAllowed}</p>
                    </div>
                    
                    <div className="p-3 border border-gray-200 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Pet Allowed :</p>
                      <p className="text-sm font-semibold text-gray-900">{specs?.petAllowed}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right Side - Booking Details Card */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sticky top-24">
                  <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-900 mb-6">
                    Booking Details
                  </h3>
                  
                  <div className="space-y-4 mb-6">
                    <div className="pb-4 border-b border-gray-200">
                      <p className="text-sm text-gray-600 mb-2">From:</p>
                      <p className="text-base font-semibold text-gray-900">{from}</p>
                    </div>
                    
                    <div className="pb-4 border-b border-gray-200">
                      <p className="text-sm text-gray-600 mb-2">To:</p>
                      <p className="text-base font-semibold text-gray-900">{to}</p>
                    </div>
                    
                    <div className="pb-4">
                      <p className="text-sm text-gray-600 mb-2">Price:</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ₹{price?.toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Inc. of Taxes*</p>
                    </div>
                  </div>
                  
                  <Button
                    variant="default"
                    size="lg"
                    fullWidth
                    onClick={handleBookNow}
                    className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold text-base py-3 rounded-lg shadow-md transition-all duration-200"
                  >
                    Book Now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CarDetails;